import React, { ReactNode } from 'react';
import {
    Box,
    CircularProgress,
    SxProps,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import DocumentIcon from '../DocumentIcon/DocumentIcon';
import UploadButton from '../UploadButton/UploadButton';
import DocumentActions, { DocumentAction } from '../DocumentActions/DocumentActions';
import useDossierDocumentFilesApi, {
    type DossierFichier,
} from '../../../hooks/useDossierDocumentFilesApi';
import { Document } from '../../../providers/types';
import DocumentTypeAlert from '../DocumentTypeAlert/DocumentTypeAlert';

export type DocumentItemProps = {
    uploadable: boolean;
    smallUploadableBtn?: boolean;
    uploading: boolean;
    hover?: boolean;
    highlight?: boolean;
    fileInputRef?: React.RefObject<HTMLLabelElement>;
    actions?: Array<DocumentAction>;
    color?: 'primary';
    sx?: SxProps;
    labelSx?: SxProps;
    showUploadBtn?: boolean;
    customAction?: ReactNode;
    actionWrapperSxProps?: SxProps;
    file?: DossierFichier;
    clickOnCard?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
} & Document;

const DocumentItem: React.FunctionComponent<DocumentItemProps> = function (props) {
    const {
        label,
        fileName,
        type,
        status = 'pending',
        date,
        feedback,
        uploadable = false,
        uploading = false,
        smallUploadableBtn = false,
        hover = false,
        highlight = false,
        fileInputRef,
        actions = [],
        color: colorProp,
        sx,
        labelSx,
        showUploadBtn = false,
        customAction = null,
        actionWrapperSxProps,
        file,
        clickOnCard = () => {},
    } = props;
    const { getColorByStatus, getFileIcon } = useDossierDocumentFilesApi();
    const color = feedback ? getColorByStatus(status) : colorProp;
    const { palette, breakpoints } = useTheme();
    const downMd = useMediaQuery(breakpoints.down('md'));

    const avatarWrapperSx = { alignSelf: downMd ? 'flex-start' : 'auto', padding: '10px 8px' };

    const nameSx = {
        color: status === 'success' ? palette.primary.dark : 'text.primary',
        fontSize: '14px',
        lineHeight: '17px',
        fontWeight: '500',
        ...labelSx,
    };

    const dateSx = {
        color: 'text.secondary',
        fontSize: '13px',
        lineHeight: '16px',
        fontWeight: '500',
    };

    const fileNameSx = {
        color: 'text.gris70',
    };

    const customActionWrapperSx = {
        marginTop: '5px',
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '0 8px',
                alignItems: 'center',
                background: highlight ? palette.grey[100] : 'none',
                border: `solid 1px ${palette.grey[300]}`,
                borderRadius: '12px',
                padding: '8px',
                transition: '0.1s ease',
                boxShadow: hover ? `0px 2px 2.5px 0px ${palette.grey[200]}` : 'none',
                cursor: uploadable ? 'pointer' : 'auto',
                ...sx,
            }}
            data-testid="document"
            onClick={(e) => clickOnCard(e)}
        >
            <Tooltip
                title={feedback}
                arrow
                placement="top"
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={avatarWrapperSx}>
                        <DocumentIcon
                            color={color}
                            icon={type ? getFileIcon(type) : 'file'}
                        />
                        {file && (
                            <DocumentTypeAlert
                                file={file}
                                status={status}
                            />
                        )}
                    </Box>
                </Box>
            </Tooltip>
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: downMd ? 'column' : 'row',
                            alignItems: downMd ? 'flex-start' : 'center',
                            gap: downMd ? '4px 0' : '0 4px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '0 4px' }}>
                            <Typography
                                component="span"
                                sx={nameSx}
                            >
                                {label}
                            </Typography>
                            {date && (
                                <Typography
                                    component="span"
                                    sx={dateSx}
                                >
                                    {date.toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    })}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {fileName && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: downMd ? 'column' : 'row',
                                alignItems: downMd ? 'flex-start' : 'center',
                                gap: downMd ? '4px 0' : '0 4px',
                            }}
                        >
                            <Typography
                                component="span"
                                sx={fileNameSx}
                                variant="l2dbodyxs"
                            >
                                {fileName}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        padding: '0 6.5px',
                        gap: '0 20px',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        justifyContent: 'end',
                        width: '100%',
                        ...actionWrapperSxProps,
                    }}
                >
                    {uploading && <CircularProgress size="16px" />}
                    {showUploadBtn && !downMd && !uploading && uploadable && fileInputRef && (
                        <UploadButton
                            fileInputRef={fileInputRef}
                            smallUploadableBtn={smallUploadableBtn}
                        />
                    )}
                    {!uploading && actions.length > 0 && <DocumentActions actions={actions} />}
                    {customAction && <Box sx={customActionWrapperSx}>{customAction}</Box>}
                </Box>
            </Box>
        </Box>
    );
};

export default DocumentItem;
